/**
 * useQueryTemplateData Hook
 *
 * Fetches chart data by executing a query definition stored on the component.
 * Handles loading states, error handling, and optional auto-refresh.
 *
 * Created: January 2026
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChartDataSource, ChartQueryDefinition, CompositeQueryDefinition } from '@/types/chartDataSource';
import { executeQueryDefinition, executeCompositeQueryDefinition } from '@/services/api/queryTemplateApi';
import { queryTemplateApi } from '@/services/api/queryTemplateApi';
import { schemaApi } from '@/services/api/schemaApi';
import { groupApi } from '@/services/api/groupApi';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { registerRefetch, unregisterRefetch, useRefreshStore } from '@/stores/data/refresh-store';
import { workflowApi } from '@/services/api/workflowApi';
import { useTrace } from '@/hooks/useTrace';
import { useDebugStore } from '@/stores/ai/debug-store';
import { useAuthStore } from '@/stores/auth/auth-store';
import { getValidatedExplicitOrganization } from '@/services/api/client';
import { isInertHomeAssistantPlaceholderBinding } from '@/utils/homeAssistantPlaceholder';
import type { CatalogSourceReconnectReason } from '@/types/componentDataBinding';

/** Minimum auto-refresh interval to prevent accidental polling storms */
const MIN_REFRESH_INTERVAL_MS = 5_000; // 5 seconds

export const PERSONAL_SPACE_BINDING_ORGANIZATION_REQUIRED_ERROR =
  'Organization context required for this data binding. Reconnect it so both dataSource.organizationId and queryDefinition.organizationId are saved with the binding.';

const PERSONAL_SPACE_COMPOSITE_BINDING_ORGANIZATION_REQUIRED_ERROR =
  'Organization context required for this data binding. Reconnect it so dataSource.organizationId is saved with the binding.';

const PERSONAL_SPACE_BINDING_ORGANIZATION_MISMATCH_ERROR =
  'Organization context required for this data binding. Reconnect it so dataSource.organizationId and queryDefinition.organizationId match.';

const PERSONAL_SPACE_BINDING_AUTHORITY_MISMATCH_ERROR =
  'Organization context required for this data binding. Reconnect it so the saved organization matches the template, schema, and group metadata.';

/**
 * Module-level cache of template IDs that returned 404.
 * Prevents every pin/chart component from independently hitting the same missing template.
 * Cleared on page navigation so stale entries don't persist forever.
 */
const missingTemplateIds = new Set<string>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isChartDebugEnabled(): boolean {
  return typeof window !== 'undefined'
    && window.localStorage.getItem('terramatrix_chart_debug') === 'true';
}

function getRowKeyUnion(rows: Record<string, unknown>[]): string[] {
  return [...new Set(rows.flatMap((row) => Object.keys(row)))];
}

function getErrorResponseDetails(err: unknown): { status?: number; message?: string } {
  const errorRecord = isRecord(err) ? err : {};
  const response = isRecord(errorRecord.response) ? errorRecord.response : {};
  const responseData = isRecord(response.data) ? response.data : {};
  const status = typeof response.status === 'number'
    ? response.status
    : typeof errorRecord.status === 'number'
      ? errorRecord.status
      : undefined;
  const message = typeof responseData.message === 'string'
    ? responseData.message
    : typeof responseData.error === 'string'
      ? responseData.error
      : undefined;

  return { status, message };
}

function normalizeOptionalString(value: string | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : null;
}

function isCatalogSourceReconnectReason(
  value: unknown,
): value is CatalogSourceReconnectReason {
  return (
    value === 'missing-source-scope' ||
    value === 'source-inaccessible' ||
    value === 'source-owner-scope-mismatch' ||
    value === 'source-permission-denied' ||
    value === 'source-write-authority-required' ||
    value === 'source-unavailable'
  );
}

function getErrorSourceReconnectReason(
  err: unknown,
  status?: number,
): CatalogSourceReconnectReason | undefined {
  const errorRecord = isRecord(err) ? err : {};
  const response = isRecord(errorRecord.response) ? errorRecord.response : {};
  const responseData = isRecord(response.data) ? response.data : {};
  const details = isRecord(responseData.details)
    ? responseData.details
    : isRecord(errorRecord.details)
      ? errorRecord.details
      : {};
  const diagnostics = isRecord(details.diagnostics) ? details.diagnostics : {};
  const explicitReason =
    isCatalogSourceReconnectReason(details.reconnectReason)
      ? details.reconnectReason
      : isCatalogSourceReconnectReason(diagnostics.reconnectReason)
        ? diagnostics.reconnectReason
        : undefined;

  if (explicitReason) {
    return explicitReason;
  }

  if (status === 403) {
    return 'source-permission-denied';
  }

  if (status === 404) {
    return 'source-unavailable';
  }

  return undefined;
}

function getPersistedDataSourceOrganizationId(
  dataSource: ChartDataSource | undefined,
): string | null {
  return normalizeOptionalString(dataSource?.organizationId);
}

function getPersistedQueryDefinitionOrganizationId(
  dataSource: ChartDataSource | undefined,
): string | null {
  return normalizeOptionalString(dataSource?.queryDefinition?.organizationId);
}

function getPersistedQueryOrganizationId(
  dataSource: ChartDataSource | undefined,
): string | null {
  return getPersistedDataSourceOrganizationId(dataSource)
    ?? getPersistedQueryDefinitionOrganizationId(dataSource);
}

interface PersonalSpaceBindingScopeValidationResult {
  requiresExplicitOrganizationScope: boolean;
  organizationId: string | null;
  error: string | null;
}

interface OrganizationScope {
  organizationId: string;
}

type ScopeResolution =
  | { kind: 'none' }
  | { kind: 'single'; scope: OrganizationScope }
  | { kind: 'ambiguous' };

function createScope(organizationId: string | undefined): OrganizationScope | null {
  const normalizedOrganizationId = normalizeOptionalString(organizationId);
  return normalizedOrganizationId ? { organizationId: normalizedOrganizationId } : null;
}

function collectSingleScope(scopes: OrganizationScope[]): ScopeResolution {
  if (scopes.length === 0) {
    return { kind: 'none' };
  }

  const organizationIds = new Set(scopes.map((scope) => scope.organizationId));
  if (organizationIds.size !== 1) {
    return { kind: 'ambiguous' };
  }

  const [organizationId] = Array.from(organizationIds);
  return organizationId
    ? { kind: 'single', scope: { organizationId } }
    : { kind: 'none' };
}

function getScopedAxiosConfig(organizationId: string): { headers: { 'X-Organization-Id': string } } {
  return {
    headers: {
      'X-Organization-Id': organizationId,
    },
  };
}

async function resolveTemplateScope(
  templateId: string,
  organizationId: string,
): Promise<ScopeResolution> {
  try {
    const template = await queryTemplateApi.getById(templateId, { organizationId });
    const scope = createScope(template?.organizationId);
    return scope ? { kind: 'single', scope } : { kind: 'none' };
  } catch {
    return { kind: 'none' };
  }
}

async function resolveSchemaScope(
  schemaId: string,
  organizationId: string,
): Promise<ScopeResolution> {
  try {
    const schema = await schemaApi.getSchema(schemaId, getScopedAxiosConfig(organizationId));
    const scope = createScope(schema.organizationId);
    return scope ? { kind: 'single', scope } : { kind: 'none' };
  } catch {
    return { kind: 'none' };
  }
}

async function resolveGroupScope(groupId: string): Promise<ScopeResolution> {
  const groupScopes: OrganizationScope[] = [];

  try {
    const group = await groupApi.getGroup(groupId);
    const groupScope = createScope(group.organizationId);
    if (groupScope) {
      groupScopes.push(groupScope);
    }

    const assignedScopes = (group.assignedOrganizations ?? [])
      .map((assignment) => createScope(assignment.organizationId))
      .filter((scope): scope is OrganizationScope => scope !== null);
    groupScopes.push(...assignedScopes);
  } catch {
    // Continue with the assigned-organization lookup below.
  }

  try {
    const assignedOrganizations = await groupApi.getAssignedOrganizations(groupId);
    const assignedScopes = assignedOrganizations
      .map((assignment) => createScope(assignment.organizationId))
      .filter((scope): scope is OrganizationScope => scope !== null);
    groupScopes.push(...assignedScopes);
  } catch {
    // Missing group metadata leaves the binding unproven.
  }

  return collectSingleScope(groupScopes);
}

async function resolveQueryDefinitionAuthoritativeScope(
  queryDefinition: ChartQueryDefinition | undefined,
  organizationId: string,
): Promise<ScopeResolution> {
  const authoritativeScopes: OrganizationScope[] = [];
  const schemaId = normalizeOptionalString(queryDefinition?.schemaId);
  const groupId = normalizeOptionalString(queryDefinition?.groupId);

  if (schemaId) {
    const schemaScope = await resolveSchemaScope(schemaId, organizationId);
    if (schemaScope.kind !== 'single') {
      return schemaScope;
    }
    authoritativeScopes.push(schemaScope.scope);
  }

  if (groupId) {
    const groupScope = await resolveGroupScope(groupId);
    if (groupScope.kind === 'ambiguous') {
      return { kind: 'ambiguous' };
    }
    if (groupScope.kind === 'single') {
      authoritativeScopes.push(groupScope.scope);
    }
  }

  return collectSingleScope(authoritativeScopes);
}

async function resolvePersonalSpaceAuthoritativeScope(
  dataSource: ChartDataSource | undefined,
  templateId: string | undefined,
  organizationId: string,
): Promise<ScopeResolution> {
  const authoritativeScopes: OrganizationScope[] = [];

  if (templateId) {
    const templateScope = await resolveTemplateScope(templateId, organizationId);
    if (templateScope.kind !== 'single') {
      return templateScope;
    }
    authoritativeScopes.push(templateScope.scope);
  }

  if (dataSource?.type === 'composite-query') {
    const sourceQueries = dataSource.compositeQueryDefinition?.sources ?? [];
    for (const source of sourceQueries) {
      const sourceScope = await resolveQueryDefinitionAuthoritativeScope(
        source.query,
        organizationId,
      );
      if (sourceScope.kind === 'ambiguous') {
        return { kind: 'ambiguous' };
      }
      if (sourceScope.kind === 'single') {
        authoritativeScopes.push(sourceScope.scope);
      }
    }
  } else {
    const queryScope = await resolveQueryDefinitionAuthoritativeScope(
      dataSource?.queryDefinition,
      organizationId,
    );
    if (queryScope.kind === 'ambiguous') {
      return { kind: 'ambiguous' };
    }
    if (queryScope.kind === 'single') {
      authoritativeScopes.push(queryScope.scope);
    }
  }

  return collectSingleScope(authoritativeScopes);
}

async function validatePersonalSpaceBindingOrganizationScope(
  dataSource: ChartDataSource | undefined,
  templateId: string | undefined,
): Promise<PersonalSpaceBindingScopeValidationResult> {
  const requiresExplicitOrganizationScope =
    !!templateId ||
    dataSource?.type === 'query-template' ||
    dataSource?.type === 'timestamp-aggregate' ||
    dataSource?.type === 'composite-query';

  if (!requiresExplicitOrganizationScope) {
    return {
      requiresExplicitOrganizationScope: false,
      organizationId: null,
      error: null,
    };
  }

  const dataSourceOrganizationId = getPersistedDataSourceOrganizationId(dataSource);
  if (dataSource?.type === 'composite-query') {
    if (!dataSourceOrganizationId) {
      return {
        requiresExplicitOrganizationScope: true,
        organizationId: null,
        error: PERSONAL_SPACE_COMPOSITE_BINDING_ORGANIZATION_REQUIRED_ERROR,
      };
    }

    const authoritativeScope = await resolvePersonalSpaceAuthoritativeScope(
      dataSource,
      templateId,
      dataSourceOrganizationId,
    );

    return authoritativeScope.kind === 'single' &&
      authoritativeScope.scope.organizationId === dataSourceOrganizationId
      ? {
          requiresExplicitOrganizationScope: true,
          organizationId: dataSourceOrganizationId,
          error: null,
        }
      : {
          requiresExplicitOrganizationScope: true,
          organizationId: null,
          error: PERSONAL_SPACE_BINDING_AUTHORITY_MISMATCH_ERROR,
        };
  }

  const queryDefinitionOrganizationId = getPersistedQueryDefinitionOrganizationId(dataSource);
  if (!dataSourceOrganizationId || !queryDefinitionOrganizationId) {
    return {
      requiresExplicitOrganizationScope: true,
      organizationId: null,
      error: PERSONAL_SPACE_BINDING_ORGANIZATION_REQUIRED_ERROR,
    };
  }

  if (dataSourceOrganizationId !== queryDefinitionOrganizationId) {
    return {
      requiresExplicitOrganizationScope: true,
      organizationId: null,
      error: PERSONAL_SPACE_BINDING_ORGANIZATION_MISMATCH_ERROR,
    };
  }

  const authoritativeScope = await resolvePersonalSpaceAuthoritativeScope(
    dataSource,
    templateId,
    dataSourceOrganizationId,
  );

  return authoritativeScope.kind === 'single' &&
    authoritativeScope.scope.organizationId === dataSourceOrganizationId
    ? {
        requiresExplicitOrganizationScope: true,
        organizationId: dataSourceOrganizationId,
        error: null,
      }
    : {
        requiresExplicitOrganizationScope: true,
        organizationId: null,
        error: PERSONAL_SPACE_BINDING_AUTHORITY_MISMATCH_ERROR,
      };
}

function getQueryDefinitionFetchKey(
  queryDefinition: ChartQueryDefinition | undefined,
): string {
  return JSON.stringify(queryDefinition ?? null);
}

function getWorkflowFetchKey(
  dataSource: ChartDataSource | undefined,
): string | null {
  return dataSource?.workflowConfig?.workflowId
    ? `wf:${dataSource.workflowConfig.workflowId}:${JSON.stringify(dataSource.workflowConfig.parameters ?? {})}`
    : null;
}

function getCompositeFetchKey(
  compositeQueryDefinition: CompositeQueryDefinition | undefined,
): string | null {
  return compositeQueryDefinition
    ? `composite:${JSON.stringify(compositeQueryDefinition)}`
    : null;
}

function getQueryExecutionScopeKey(
  dataSource: ChartDataSource | undefined,
): string {
  const organizationId = getPersistedQueryOrganizationId(dataSource);
  return organizationId ? `org:${organizationId}` : 'org:none';
}

function buildFetchKey(
  dataSource: ChartDataSource | undefined,
  templateId: string | undefined,
): string {
  const workflowKey = getWorkflowFetchKey(dataSource);
  if (workflowKey) {
    return workflowKey;
  }

  const executionScopeKey = getQueryExecutionScopeKey(dataSource);
  const compositeKey = getCompositeFetchKey(dataSource?.compositeQueryDefinition);
  if (compositeKey) {
    return `${compositeKey}:${executionScopeKey}`;
  }

  if (templateId) {
    return `tpl:${templateId}:${executionScopeKey}`;
  }

  return `query:${getQueryDefinitionFetchKey(dataSource?.queryDefinition)}:${executionScopeKey}`;
}

interface UseQueryTemplateDataOptions {
  /** Data source configuration from component properties */
  dataSource: ChartDataSource | undefined;
  /** Component ID for logging/caching */
  componentId: string;
  /** Whether to execute the query (default: true) */
  enabled?: boolean;
  /** Initial data to use before fetch completes */
  initialData?: Record<string, unknown>[];
  /** Direct template execution mode — uses queryTemplateApi.execute() instead of AI pipeline */
  templateId?: string;
}

interface UseQueryTemplateDataResult {
  /** Fetched data array */
  data: Record<string, unknown>[] | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Source-level resolver code when the backend reported one */
  sourceErrorCode?: CatalogSourceReconnectReason;
  /** Manually trigger a data refresh */
  refetch: () => Promise<void>;
  /** Timestamp of last successful fetch */
  lastFetched: Date | null;
  /** Execution metadata */
  metadata?: {
    rowCount: number;
    executionTimeMs: number;
    cached?: boolean;
    cacheKey?: string;
    skipCache?: boolean;
    templateUserOrgId?: string;
    templateDataOrgId?: string;
    templateQuerySummary?: {
      schemaId?: string;
      groupId?: string;
      measures?: string[];
      filters?: string[];
      orderBy?: string[];
      groupBy?: string[];
      timeRange?: string;
      limit?: number;
    };
  };
}

function summarizeQueryDefinition(queryDefinition: ChartQueryDefinition | undefined) {
  if (!queryDefinition) return {};

  return {
    measures: queryDefinition.measures?.map(measure =>
      `${measure.aggregation}:${measure.field}${measure.alias ? ` as ${measure.alias}` : ''}`
    ),
    filters: queryDefinition.filters ? Object.keys(queryDefinition.filters) : undefined,
    orderBy: queryDefinition.orderBy?.map(order => `${order.field}:${order.direction}`),
    groupBy: queryDefinition.groupBy?.map(group =>
      `${group.field}${group.granularity ? `:${group.granularity}` : ''}`
    ),
    limit: queryDefinition.limit,
    hasAiPipeline: !!queryDefinition.aiGeneratedPipeline?.length,
  };
}

/**
 * Hook to fetch chart data from a query template definition
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useQueryTemplateData({
 *   dataSource: component.properties.dataSource,
 *   componentId: component.id,
 * });
 *
 * if (isLoading) return <Loading />;
 * if (error) return <Error message={error} />;
 * return <Chart data={data} />;
 * ```
 */
export function useQueryTemplateData({
  dataSource,
  componentId,
  enabled = true,
  initialData,
  templateId,
}: UseQueryTemplateDataOptions): UseQueryTemplateDataResult {
  // Unified tracing — called unconditionally (React Rules of Hooks)
  const {
    isEnabled: isTraceEnabled,
    startTrace,
    startSpan,
    endSpan,
  } = useTrace(componentId);

  const [data, setData] = useState<Record<string, unknown>[] | null>(
    initialData || null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceErrorCode, setSourceErrorCode] = useState<
    CatalogSourceReconnectReason | undefined
  >();
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [metadata, setMetadata] = useState<UseQueryTemplateDataResult['metadata']>();
  const authInitialized = useAuthStore((state) => state.initialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken);
  const authReady = authInitialized &&
    isAuthenticated &&
    typeof accessToken === 'string' &&
    accessToken.length > 0;

  // Track mounted state for cleanup
  const mountedRef = useRef(true);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Stable ref for dataSource to avoid object-identity re-renders
  const dataSourceRef = useRef(dataSource);
  dataSourceRef.current = dataSource;

  // Fetch-deduplication guard — prevents overlapping requests
  const isFetchingRef = useRef(false);

  // Persistent error flag — stops auto-refresh retries on non-transient errors (e.g. invalid IDs)
  const persistentErrorRef = useRef(false);

  // Tracks the query key that was last successfully fetched.
  // Prevents redundant re-fetches when re-renders produce the same query definition.
  const lastFetchedKeyRef = useRef<string | null>(null);

  // Stable serialized key for query definition — prevents infinite loops
  // caused by parent re-renders creating new object references with identical content.
  // JSON.stringify produces a value-based string so React's Object.is() compares content not reference.
  const queryDefKey = useMemo(
    () => getQueryDefinitionFetchKey(dataSource?.queryDefinition),
    [dataSource?.queryDefinition]
  );
  const executionScopeKey = getQueryExecutionScopeKey(dataSource);

  // Composite fetch key: combines query def + templateId + workflow config so we detect any change
  const workflowKey = getWorkflowFetchKey(dataSource);
  const compositeKey = getCompositeFetchKey(dataSource?.compositeQueryDefinition);
  const fetchKey = workflowKey || (
    compositeKey
      ? `${compositeKey}:${executionScopeKey}`
      : templateId
        ? `tpl:${templateId}:${executionScopeKey}`
        : `query:${queryDefKey}:${executionScopeKey}`
  );

  // Stable boolean for whether we should execute (primitive deps only)
  const hasQueryDef = queryDefKey !== 'null';
  const hasWorkflowConfig = !!dataSource?.workflowConfig?.workflowId;
  const hasCompositeDef = !!dataSource?.compositeQueryDefinition?.sources?.length;
  const isInertHomeAssistantPlaceholder = isInertHomeAssistantPlaceholderBinding(
    dataSource,
    templateId,
  );
  const shouldExecute = useMemo(
    () => enabled && authReady && !isInertHomeAssistantPlaceholder && (
      !!templateId ||
      (dataSource?.type === 'query-template' && hasQueryDef) ||
      (dataSource?.type === 'timestamp-aggregate' && hasQueryDef) ||
      (dataSource?.type === 'workflow' && hasWorkflowConfig) ||
      (dataSource?.type === 'composite-query' && hasCompositeDef)
    ),
    [
      authReady,
      enabled,
      isInertHomeAssistantPlaceholder,
      templateId,
      dataSource?.type,
      hasQueryDef,
      hasWorkflowConfig,
      hasCompositeDef,
    ]
  );

  const recordQueryDiagnostic = useCallback((
    status: 'skipped' | 'started' | 'success' | 'error',
    details: {
      reason?: string;
      data?: Record<string, unknown>[] | null;
      metadata?: UseQueryTemplateDataResult['metadata'];
      error?: string;
      httpStatus?: number;
      force?: boolean;
      dataSourceOverride?: ChartDataSource;
    } = {},
  ) => {
    const debug = useDebugStore.getState();
    if (!debug.isEnabled || !debug.planDebugData) return;

    const currentDataSource = details.dataSourceOverride ?? dataSourceRef.current;
    const qd = currentDataSource?.queryDefinition;
    const rowKeys = details.data?.[0] && typeof details.data[0] === 'object'
      ? Object.keys(details.data[0]).slice(0, 20)
      : [];
    const mode = currentDataSource?.type === 'workflow'
      ? 'workflow'
      : templateId
        ? 'template'
        : 'pipeline';
    const querySummary = summarizeQueryDefinition(qd);

    debug.recordRuntimeQueryTemplateDiagnostic({
      componentId,
      status,
      reason: details.reason,
      mode,
      dataSourceType: currentDataSource?.type,
      queryTemplateId: templateId,
      schemaId: qd?.schemaId,
      groupId: qd?.groupId,
      organizationId: qd?.organizationId,
      ...querySummary,
      authUserPresent: !!useAuthStore.getState().user?.id,
      shouldExecute,
      force: details.force,
      rowCount: details.data?.length,
      rowKeys,
      executionTimeMs: details.metadata?.executionTimeMs,
      cached: details.metadata?.cached,
      cacheKey: details.metadata?.cacheKey,
      skipCache: details.metadata?.skipCache,
      templateUserOrgId: details.metadata?.templateUserOrgId,
      templateDataOrgId: details.metadata?.templateDataOrgId,
      templateQuerySummary: details.metadata?.templateQuerySummary,
      httpStatus: details.httpStatus,
      error: details.error,
    });
  }, [componentId, shouldExecute, templateId]);

  // Diagnostic: warn when hook is enabled but won't execute (missing dataSource config)
  useEffect(() => {
    if (enabled && !shouldExecute && !isInertHomeAssistantPlaceholder) {
      console.warn('[useQueryTemplateData] Hook enabled but not executing', {
        componentId,
        authInitialized,
        isAuthenticated,
        hasAccessToken: typeof accessToken === 'string' && accessToken.length > 0,
        dataSourceType: dataSource?.type,
        hasQueryDef,
        hasWorkflowConfig,
        templateId,
      });
    }
  }, [
    accessToken,
    authInitialized,
    componentId,
    dataSource?.type,
    enabled,
    hasQueryDef,
    hasWorkflowConfig,
    isAuthenticated,
    isInertHomeAssistantPlaceholder,
    shouldExecute,
    templateId,
  ]);

  /**
   * Execute the query and update state.
   * @param force — bypass the "already fetched this key" guard (used by manual refetch)
   */
  const executeQuery = useCallback(async (force = false) => {
    const currentDataSource = dataSourceRef.current;
    if (isInertHomeAssistantPlaceholderBinding(currentDataSource, templateId)) {
      if (mountedRef.current) {
        setIsLoading(false);
        setError(null);
      }
      recordQueryDiagnostic('skipped', {
        reason: 'inert-home-assistant-placeholder',
        force,
        dataSourceOverride: currentDataSource,
      });
      return;
    }

    if (
      !templateId &&
      !currentDataSource?.queryDefinition &&
      !(currentDataSource?.type === 'workflow' && currentDataSource?.workflowConfig?.workflowId) &&
      !(currentDataSource?.type === 'composite-query' && currentDataSource?.compositeQueryDefinition)
    ) {
      return;
    }

    // Prevent overlapping fetches
    if (isFetchingRef.current) {
      console.debug('[useQueryTemplateData] Skipped (already fetching)', { componentId });
      recordQueryDiagnostic('skipped', {
        reason: 'already-fetching',
        force,
        dataSourceOverride: currentDataSource,
      });
      return;
    }

    // Skip if we already successfully fetched this exact query (unless forced)
    const currentFetchKey = buildFetchKey(currentDataSource, templateId);
    if (!force && lastFetchedKeyRef.current === currentFetchKey) {
      console.debug('[useQueryTemplateData] Skipped (same key)', { componentId });
      recordQueryDiagnostic('skipped', {
        reason: 'same-fetch-key',
        force,
        dataSourceOverride: currentDataSource,
      });
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);
    setSourceErrorCode(undefined);
    recordQueryDiagnostic('started', {
      force,
      dataSourceOverride: currentDataSource,
    });

    // Unified tracing: create thread and span for this data request
    let traceThreadId: string | null = null;
    let traceSpanId: string | null = null;
    if (isTraceEnabled) {
      traceThreadId = await startTrace('data_source', `${currentDataSource?.type || 'unknown'}:${componentId}`).catch(() => null);
      if (traceThreadId) {
        traceSpanId = await startSpan(traceThreadId, 'ui_data_request', `Data fetch: ${componentId}`, {
          detail: {
            componentId,
            dataSourceType: currentDataSource?.type,
            workflowId: currentDataSource?.workflowConfig?.workflowId,
            workflowName: currentDataSource?.workflowConfig?.workflowName,
            schemaId: currentDataSource?.queryDefinition?.schemaId,
            templateId,
            refreshInterval: currentDataSource?.refreshInterval,
          },
        }).catch(() => null);
      }
    }

    try {
      let resultData: Record<string, unknown>[];
      let resultMetadata: UseQueryTemplateDataResult['metadata'];

      // Cross-org support: in personal space, pass the component's org context
      // so each component can fetch data from its own org independently.
      // Do not attempt an unscoped metadata lookup here: data-service requests
      // require explicit org scope, and personal-space bindings must persist it.
      const orgState = useOrganizationStore.getState();
      const isPersonalSpace = orgState.currentContext?.type === 'user';
      const personalSpaceBindingScope = isPersonalSpace
        ? await validatePersonalSpaceBindingOrganizationScope(currentDataSource, templateId)
        : {
            requiresExplicitOrganizationScope: false,
            organizationId: null,
            error: null,
          };
      if (personalSpaceBindingScope.error) {
        persistentErrorRef.current = true;
        setError(personalSpaceBindingScope.error);
        recordQueryDiagnostic('error', {
          error: personalSpaceBindingScope.error,
          force,
          dataSourceOverride: currentDataSource,
        });
        return;
      }

      const componentOrgId = personalSpaceBindingScope.organizationId;
      const validatedComponentOrganization =
        isPersonalSpace && componentOrgId
          ? await getValidatedExplicitOrganization({
              organizationId: componentOrgId,
            })
          : null;
      const requiresExplicitOrganizationScope = isPersonalSpace && (
        !!templateId ||
        currentDataSource?.type === 'query-template' ||
        currentDataSource?.type === 'timestamp-aggregate' ||
        currentDataSource?.type === 'composite-query'
      );

      if (requiresExplicitOrganizationScope && !componentOrgId) {
        persistentErrorRef.current = true;
        const errorMessage = 'This personal-space data binding is missing its source organization. Reconnect it so dataSource.organizationId or queryDefinition.organizationId is saved with the binding.';
        setError(errorMessage);
        setSourceErrorCode('missing-source-scope');
        recordQueryDiagnostic('error', {
          error: errorMessage,
          force,
          dataSourceOverride: currentDataSource,
        });
        return;
      }

      if (isPersonalSpace && componentOrgId && !validatedComponentOrganization) {
        persistentErrorRef.current = true;
        const errorMessage = 'This personal-space data binding points to an organization you can no longer access. Reconnect it to a current organization.';
        setError(errorMessage);
        setSourceErrorCode('source-inaccessible');
        recordQueryDiagnostic('error', {
          error: errorMessage,
          force,
          dataSourceOverride: currentDataSource,
        });
        return;
      }

      const crossOrgOptions = validatedComponentOrganization
        ? { organizationId: validatedComponentOrganization._id }
        : undefined;
      const queryDefinition = currentDataSource?.queryDefinition;

      console.debug('[useQueryTemplateData] Cross-org context', {
        componentId,
        contextType: orgState.currentContext?.type,
        isPersonalSpace,
        componentOrgId,
        crossOrgOptions,
        templateId,
        hasQueryDef: !!queryDefinition,
      });
      if (currentDataSource?.type === 'workflow' && currentDataSource.workflowConfig?.workflowId) {
        // Workflow Data Source mode: execute workflow synchronously via workflow service
        const wfConfig = currentDataSource.workflowConfig;
        // Pass trace headers for cross-service trace propagation
        const traceHeaders = isTraceEnabled && traceThreadId && traceSpanId
          ? { threadId: traceThreadId, parentSpanId: traceSpanId, traceLevel: isTraceEnabled ? 2 : 0 }
          : undefined;
        const response = await workflowApi.executeWorkflowDataSource(
          wfConfig.workflowId,
          wfConfig.parameters,
          wfConfig.timeoutMs,
          traceHeaders
        );
        if (!mountedRef.current) return;
        if (!response.success || !response.data) {
          setError(response.error || 'Workflow execution failed');
          return;
        }
        resultData = response.data;
        resultMetadata = response.metadata;
      } else if (currentDataSource?.type === 'composite-query' && currentDataSource.compositeQueryDefinition) {
        // Composite Query mode: backend merges multiple sources into one flat rowset
        const response = await executeCompositeQueryDefinition(
          currentDataSource.compositeQueryDefinition,
          crossOrgOptions,
        );
        if (!mountedRef.current) return;
        if (!response.success || !response.data) {
          const errorMessage = response.error || 'Composite query execution failed';
          setError(errorMessage);
          recordQueryDiagnostic('error', { error: errorMessage, force, dataSourceOverride: currentDataSource });
          return;
        }
        resultData = response.data;
        resultMetadata = response.metadata ? {
          rowCount: response.metadata.rowCount,
          executionTimeMs: response.metadata.executionTimeMs,
          cached: response.metadata.cached,
        } : undefined;
      } else if (templateId) {
        // Skip network call if another component already discovered this template is gone
        if (missingTemplateIds.has(templateId)) {
          persistentErrorRef.current = true;
          setError('Data template no longer exists');
          setSourceErrorCode('source-unavailable');
          return;
        }
        // Template execution mode: use same proven path as data panel
        const result = await queryTemplateApi.execute(templateId, {}, {
          ...crossOrgOptions,
          // Data-bound visual components need read-after-write correctness. The
          // plan runtime already handles client-side fetch dedupe, so avoid
          // serving stale Redis execution results on normal rerenders as well
          // as explicit refreshes.
          skipCache: true,
        });
        resultData = result.data;
        resultMetadata = {
          rowCount: result.metadata.rowCount,
          executionTimeMs: result.metadata.executionTimeMs,
          cached: result.metadata.cached,
          cacheKey: result.metadata.cacheKey,
          skipCache: result.metadata.skipCache,
          templateUserOrgId: result.metadata.userOrgId,
          templateDataOrgId: result.metadata.dataOrgId,
          templateQuerySummary: result.metadata.querySummary,
        };
      } else {
        // Pipeline mode: use AI-generated query definition
        const qd = queryDefinition;
        if (!qd) {
          return;
        }
        // Validate ObjectId format to avoid 400 errors from hallucinated IDs
        if (!qd.groupId) {
          persistentErrorRef.current = true;
          setError('Data connection incomplete. Use "Add Data" to reconnect this gauge.');
          return;
        }
        const objectIdRegex = /^[0-9a-fA-F]{24}$/;
        if (qd.schemaId && !objectIdRegex.test(qd.schemaId) || qd.groupId && !objectIdRegex.test(qd.groupId)) {
          persistentErrorRef.current = true;
          setError('Invalid data source configuration — schema or group ID is not valid');
          return;
        }
        const response = crossOrgOptions
          ? await executeQueryDefinition(qd, crossOrgOptions)
          : await executeQueryDefinition(qd);
        if (!mountedRef.current) return;
        if (!response.success || !response.data) {
          const errorMessage = response.error || 'Query execution failed';
          setError(errorMessage);
          recordQueryDiagnostic('error', {
            error: errorMessage,
            force,
            dataSourceOverride: currentDataSource,
          });
          return;
        }
        resultData = response.data;
        resultMetadata = response.metadata;
      }

      if (!mountedRef.current) return;

      // Flatten _id objects from MongoDB aggregation groupBy results.
      // MongoDB returns grouped data as { _id: { field_key: value }, measure: ... }
      // Charts need flat rows like { field_key: value, measure: ... }
      resultData = resultData.map((row) => {
        if (row._id && typeof row._id === 'object' && !Array.isArray(row._id)) {
          const { _id, ...rest } = row;
          return isRecord(_id) ? { ...rest, ..._id } : row;
        }
        return row;
      });

      // Mark this query as fetched BEFORE state updates to prevent re-entry
      lastFetchedKeyRef.current = currentFetchKey;

      setData(resultData);
      setSourceErrorCode(undefined);
      setLastFetched(new Date());
      useRefreshStore.getState().registerTimestamp(componentId, new Date());
      setMetadata(resultMetadata);
      recordQueryDiagnostic('success', {
        data: resultData,
        metadata: resultMetadata,
        force,
        dataSourceOverride: currentDataSource,
      });

      if (isChartDebugEnabled()) {
        console.info('[useQueryTemplateData debug]', {
          componentId,
          mode: currentDataSource?.type === 'composite-query'
            ? 'composite'
            : templateId
              ? 'template'
              : 'pipeline',
          templateId,
          dataSourceType: currentDataSource?.type,
          queryTemplateId: templateId,
          rowCount: resultData.length,
          firstRowKeys: Object.keys(resultData[0] ?? {}),
          rowKeyUnion: getRowKeyUnion(resultData),
          metadataKeys: resultMetadata ? Object.keys(resultMetadata) : [],
        });
      }

      console.debug('[useQueryTemplateData] Data fetched', {
        componentId,
        mode: templateId ? 'template' : 'pipeline',
        rowCount: resultData.length,
        executionTimeMs: resultMetadata?.executionTimeMs,
      });

      // End trace span with success
      if (traceSpanId) {
        endSpan(traceSpanId, 'ok', {
          detail: {
            rowCount: resultData.length,
            executionTimeMs: resultMetadata?.executionTimeMs,
            responseFields: resultData.length > 0 ? Object.keys(resultData[0]) : [],
          },
        }).catch(() => {});
      }
    } catch (err) {
      if (!mountedRef.current) return;

      const {status, message: serverMsg} = getErrorResponseDetails(err);
      const errorMessage = serverMsg
        || (status ? `Server returned ${status}` : null)
        || (err instanceof Error ? err.message : 'Failed to fetch chart data');
      setSourceErrorCode(getErrorSourceReconnectReason(err, status));
      setError(errorMessage);
      recordQueryDiagnostic('error', {
        error: errorMessage,
        httpStatus: status,
        force,
        dataSourceOverride: currentDataSource,
      });

      // Mark non-retryable errors as persistent to stop auto-refresh retries
      if (status === 400 || status === 403 || status === 404) {
        persistentErrorRef.current = true;
      }

      // Cache missing template IDs so other components skip the network call
      if (status === 404 && templateId) {
        missingTemplateIds.add(templateId);
      }

      const errorRecord = isRecord(err) ? err : {};
      const axiosConfig = isRecord(errorRecord.config) ? errorRecord.config : {};
      const axiosResponse = isRecord(errorRecord.response) ? errorRecord.response : {};

      // Diagnostic logging — captures full request/response details for debugging
      // 404 = template no longer exists (data issue, not code error) — use warn level
      const logFn = status === 404 ? console.warn : console.error;
      const rawMsg = typeof errorRecord.message === 'string' ? errorRecord.message : '(no message)';
      const rawType = err instanceof Error ? err.constructor.name : typeof err;
      const rawKeys = Object.keys(errorRecord).join(',');
      logFn(`[useQueryTemplateData FAIL] component=${componentId} type=${rawType} msg="${rawMsg}" keys=[${rawKeys}] url=${axiosConfig.url || '(none)'} status=${status || '(none)'}`);
      logFn('[useQueryTemplateData] Query failed — DETAILS', {
        componentId,
        url: axiosConfig.url,
        baseURL: axiosConfig.baseURL,
        status,
        responseData: axiosResponse.data,
        errorMessage,
        errorCode: errorRecord.code,
        rawErrorMessage: rawMsg,
        rawErrorType: rawType,
        rawErrorKeys: Object.keys(errorRecord),
        persistent: persistentErrorRef.current,
      });

      // End trace span with error
      if (traceSpanId) {
        endSpan(traceSpanId, 'error', {
          errorMessage,
          detail: {
            error_category: status === 401 || status === 403 ? 'auth_failure'
              : status === 404 ? 'service_unavailable'
              : status === 400 ? 'validation_error'
              : 'unknown',
            httpStatus: status,
          },
        }).catch(() => {});
      }
    } finally {
      isFetchingRef.current = false;
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [componentId, endSpan, isTraceEnabled, recordQueryDiagnostic, startSpan, startTrace, templateId]);

  // Stable ref for executeQuery — used by interval and effects to avoid resetting timers
  const executeQueryRef = useRef(executeQuery);
  executeQueryRef.current = executeQuery;

  /**
   * Manual refetch — forces a re-fetch even if the query key hasn't changed
   */
  const refetch = useCallback(async () => {
    lastFetchedKeyRef.current = null; // Reset guard so next call goes through
    await executeQueryRef.current(true);
  }, []);

  // Register refetch function in refresh registry (for badge click-to-refresh)
  // Only register when this instance is actively executing — prevents disabled hooks
  // (e.g. useDataMultiValue with enabled=false) from overwriting active registrations
  // for the same componentId.
  useEffect(() => {
    if (!shouldExecute) return;
    registerRefetch(componentId, async () => {
      lastFetchedKeyRef.current = null;
      await executeQueryRef.current(true);
    });
    return () => unregisterRefetch(componentId);
  }, [componentId, shouldExecute]);

  // Unregister timestamp on unmount
  useEffect(() => {
    return () => useRefreshStore.getState().unregisterTimestamp(componentId);
  }, [componentId]);

  // Execute query on mount and when query definition actually changes.
  // The lastFetchedKeyRef guard inside executeQuery prevents redundant fetches
  // even if this effect fires due to upstream re-renders.
  useEffect(() => {
    const currentDataSource = dataSourceRef.current;

    // New query definition → reset persistent error flag so retry is valid
    persistentErrorRef.current = false;

    console.debug('[useQueryTemplateData] Effect triggered', {
      componentId, shouldExecute, fetchKey: fetchKey.slice(0, 60),
    });
    if (shouldExecute) {
      executeQueryRef.current();
    } else if (isTraceEnabled && currentDataSource) {
      // Skipped span — record WHY shouldExecute is false
      // This is critical for debugging "component shows --" issues
      (async () => {
        try {
          const skipThreadId = await startTrace('data_source', `SKIPPED:${componentId}`);
          if (skipThreadId) {
            const skipSpanId = await startSpan(skipThreadId, 'ui_data_request', `SKIPPED: ${componentId}`, {
              detail: {
                status: 'skipped',
                reason: !enabled
                  ? 'enabled=false'
                  : !authReady
                    ? 'auth-not-ready'
                  : !templateId && !hasQueryDef && !hasWorkflowConfig
                    ? 'no_data_source_configured'
                    : `type=${currentDataSource.type}, hasQueryDef=${hasQueryDef}, hasWorkflowConfig=${hasWorkflowConfig}, hasTemplateId=${!!templateId}`,
                componentId,
                dataSourceType: currentDataSource.type,
                hasQueryDef,
                hasWorkflowConfig,
                hasTemplateId: !!templateId,
                enabled,
                authInitialized,
                isAuthenticated,
                hasAccessToken: typeof accessToken === 'string' && accessToken.length > 0,
                configSnapshot: {
                  dataSourceType: currentDataSource.type,
                  hasQueryDefinition: !!currentDataSource.queryDefinition,
                  hasWorkflowConfig: !!currentDataSource.workflowConfig,
                  workflowId: currentDataSource.workflowConfig?.workflowId,
                },
              },
            });
            if (skipSpanId) await endSpan(skipSpanId, 'ok');
          }
        } catch { /* fire-and-forget */ }
      })();
    }
    if (enabled && !shouldExecute && !isInertHomeAssistantPlaceholder) {
      recordQueryDiagnostic('skipped', {
        reason: !enabled
          ? 'enabled=false'
          : !authReady
            ? 'auth-not-ready'
          : !templateId && !hasQueryDef && !hasWorkflowConfig
            ? 'no-data-source-configured'
            : `type=${currentDataSource?.type}, hasQueryDef=${hasQueryDef}, hasWorkflowConfig=${hasWorkflowConfig}, hasTemplateId=${!!templateId}`,
      });
    }
  }, [
    accessToken,
    authInitialized,
    authReady,
    componentId,
    enabled,
    fetchKey,
    hasQueryDef,
    hasWorkflowConfig,
    isAuthenticated,
    isTraceEnabled,
    isInertHomeAssistantPlaceholder,
    recordQueryDiagnostic,
    endSpan,
    startSpan,
    startTrace,
    shouldExecute,
    templateId,
  ]);

  // Set up auto-refresh interval if configured
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // Set up new interval if refresh is enabled
    if (
      shouldExecute &&
      dataSource?.refreshInterval &&
      dataSource.refreshInterval > 0
    ) {
      const intervalMs = Math.max(dataSource.refreshInterval, MIN_REFRESH_INTERVAL_MS);

      console.debug('[useQueryTemplateData] Auto-refresh configured', {
        componentId,
        intervalMs,
        rawInterval: dataSource.refreshInterval,
      });

      refreshIntervalRef.current = setInterval(() => {
        if (persistentErrorRef.current) return; // Don't retry persistent errors (400, invalid IDs)
        console.debug('[useQueryTemplateData] Auto-refresh tick', { componentId });
        executeQueryRef.current(true); // force=true: auto-refresh should always re-fetch
      }, intervalMs);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [shouldExecute, dataSource?.refreshInterval, componentId]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    console.debug('[useQueryTemplateData] Mounted', { componentId });

    return () => {
      console.debug('[useQueryTemplateData] Unmounted', { componentId });
      mountedRef.current = false;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [componentId]);

  return {
    data,
    isLoading,
    error,
    sourceErrorCode,
    refetch,
    lastFetched,
    metadata,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a data source is a query template
 */
export function isQueryTemplateDataSource(
  dataSource: unknown
): dataSource is ChartDataSource & { queryDefinition: ChartQueryDefinition } {
  if (!isRecord(dataSource)) {
    return false;
  }

  return (
    dataSource?.type === 'query-template' &&
    isRecord(dataSource.queryDefinition)
  );
}

/**
 * Check if a data source is a workflow data source
 */
export function isWorkflowDataSource(
  dataSource: ChartDataSource | undefined
): boolean {
  return (
    dataSource?.type === 'workflow' &&
    !!dataSource.workflowConfig?.workflowId
  );
}

/**
 * Extract xAxisKey from query definition
 *
 * Uses the first groupBy field as the x-axis key
 */
export function getXAxisKeyFromQuery(
  queryDef: ChartQueryDefinition | undefined
): string {
  if (!queryDef?.groupBy || queryDef.groupBy.length === 0) {
    return 'timestamp';
  }

  const firstGroupBy = queryDef.groupBy[0];
  // MongoDB aggregation uses underscores for nested field keys in _id:
  // e.g. groupBy "metadata.timestamp" → _id: { metadata_timestamp: ... }
  // After flattening _id, the key is "metadata_timestamp"
  return firstGroupBy.field.replace(/\./g, '_') || 'timestamp';
}

/**
 * Extract lineKeys (measure fields) from query definition
 */
export function getLineKeysFromQuery(
  queryDef: ChartQueryDefinition | undefined
): string[] {
  if (!queryDef?.measures || queryDef.measures.length === 0) {
    return ['value'];
  }

  return queryDef.measures.map((m) => {
    if (m.alias) return m.alias;
    const parts = m.field.split('.');
    return parts[parts.length - 1];
  });
}

/**
 * Extract xAxisKey from a composite query definition.
 *
 * Priority:
 * 1. First field in orderBy[] (the primary sort key is almost always the time axis)
 * 2. First field mapping from the primary source
 * 3. First field mapping overall
 */
export function getXAxisKeyFromComposite(
  compositeDef: CompositeQueryDefinition | undefined
): string {
  if (!compositeDef) return 'time';
  if (compositeDef.orderBy?.[0]) return compositeDef.orderBy[0].field;
  const primaryGroupBy = compositeDef.sources
    ?.find(source => source.id === compositeDef.primarySourceId)
    ?.query.groupBy?.[0]?.field
    ?.replace(/\./g, '_');
  if (primaryGroupBy) return primaryGroupBy;
  if (!compositeDef.fields?.length) return 'time';
  const primaryField = compositeDef.fields.find(f => f.sourceId === compositeDef.primarySourceId);
  return primaryField?.alias ?? compositeDef.fields[0].alias;
}

/**
 * Extract lineKeys (series fields) from a composite query definition.
 * Returns all field aliases except the one used as the x-axis key.
 */
export function getLineKeysFromComposite(
  compositeDef: CompositeQueryDefinition | undefined
): string[] {
  if (!compositeDef) return ['value'];
  const xAxisKey = getXAxisKeyFromComposite(compositeDef);
  const fieldAliases = (compositeDef.fields || []).map(f => f.alias);
  const computedAliases = (compositeDef.computedFields || []).map(f => f.name);
  const measureAliases = (compositeDef.sources || []).flatMap(source =>
    (source.query.measures || []).map(measure => (measure.alias || measure.field).replace(/\./g, '_'))
  );
  const keys = [...new Set([...fieldAliases, ...computedAliases, ...measureAliases])].filter(a => a !== xAxisKey);
  return keys.length > 0 ? keys : ['value'];
}
